<template>
    <modal ref="modal" id="modal-extra-package" size="sm">
        <template slot="title">{{ t('balance-extra_package') }}</template>
        <template slot="body">
            <div class="form-group">
                <label>{{ t('balance-credits') }}:</label>
                <input class="form-control" type="number" v-model.number="amount" />
            </div>
            <div class="form-group">
                <label>{{ plan && plan.currency ? plan.currency : 'R$' }}</label>
                <input class="form-control" :disabled="!(user && user.global_admin)" type="tel" v-money="money" v-model="price" />
            </div>
            <div class="form-group" v-if="user && user.global_admin">
                <label>{{ t('balance-description') }}:</label>
                <input class="form-control" type="text" v-model="description" />
            </div>
        </template>
        <template slot="footer">
            <div class="d-flex justify-content-end gap-md">
                <KvButton variant="outlined" @click="close">{{ t('cancel') || 'Cancelar' }}</KvButton>
                <kv-tooltip :text="disabledMessage" :disabled="!isButtonDisabled">
                    <KvButton 
                        variant="filled" 
                        :loading="loading" 
                        :disabled="isButtonDisabled"
                        @click.prevent="onClickUpdate"
                    >
                        {{ t('balance-contract_button') }}
                    </KvButton>
                </kv-tooltip>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from "../../components/modal/modal";
import KvButton from "../../components/form/kv-button.vue";
import KvTooltip from "../../components/ui/kv-tooltip.vue";
import keviApi from "../../keviApi";

const moneyConfig = {
    decimal: ",",
    thousands: ".",
    prefix: "R$ ",
    suffix: "",
    precision: 2,
    masked: false,
};

export default {
    components: { Modal, KvButton, KvTooltip },
    props: {
        user: { type: Object, default: () => ({}) },
        place: { type: Object, default: () => ({}) },
        plan: { type: Object, default: () => ({}) },
        saving: { type: Boolean, default: false }
    },
    data() {
        return {
            amount: "",
            price: 0,
            description: "",
            money: moneyConfig,
            loading: false,
        };
    },
    computed: {
        wasChanged() {
            return !!this.amount && parseInt(this.amount) > 0;
        },
        isButtonDisabled() {
            return !this.wasChanged;
        },
        disabledMessage() {
            if (!this.wasChanged) return this.t('balance-inform_credits_amount');
            return "";
        }
    },
    watch: {
        amount(newVal) {
            const amount = parseInt(newVal);
            if (amount && this.plan?.pricetable_extra_credits?.length > 0) {
                let pricePerUnit = this.plan.pricetable_extra_credits[this.plan.pricetable_extra_credits.length - 1];
                const filtered = this.plan.pricetable_extra_credits.filter((i) => parseInt(i.interval) >= amount);
                if (filtered.length) pricePerUnit = filtered[0];
                this.price = (amount * parseFloat(pricePerUnit?.unitValue || 0)).toFixed(2);
            } else {
                this.price = "0.00";
            }
        }
    },
    methods: {
        open() { 
            this.amount = "";
            this.price = 0;
            this.description = "";
            this.$refs.modal.open(); 
        },
        close() { this.$refs.modal.close(); },
        onClickUpdate() {
            this.submit();
        },
        async submit() {
            if (this.loading) return;
            this.loading = true;
            try {
                const isAdmin = this.user && this.user.global_admin;
                const endpoint = isAdmin ? 'transactions/save' : 'invoices/request-extra-credits';
                const cleanPrice = typeof this.price === 'string' 
                    ? parseFloat(this.price.replace(/[^\d]/g, '')) / 100 
                    : this.price;

                const payload = isAdmin 
                    ? { 
                        places_id: this.place.id, 
                        amount: this.amount, 
                        price: cleanPrice, 
                        description: this.description 
                    }
                    : { 
                        amount: this.amount, 
                        total: cleanPrice
                    };

                const options = keviApi.getOptions(endpoint, 'POST', payload);
                const response = await this.$http(options);

                if (response.status === 200 || response.status === 201) {
                    if (isAdmin) {
                        this.$store.dispatch('notification/success', this.t('balance-credits_added_success'));
                        this.close();
                        this.$emit('success');
                    } else {
                        this.$store.dispatch('notification/success', this.t('balance-generating_invoice'));
                        this.openWhatsAppExtra();
                    }
                } else {
                    this.$store.dispatch('notification/error', this.t('balance-error_processing_request'));
                }
            } catch (err) {
                console.error(err);
                this.$store.dispatch('notification/error', this.t('balance-server_communication_error'));
            } finally {
                this.loading = false;
            }
        },
        openWhatsAppExtra() {
            const phone = "5548988418958"; 
            const establishment = this.t('balance-whatsapp_msg_establishment').replace('{name}', this.place?.name || this.t('balance-not_informed'));
            const requester = this.t('balance-whatsapp_msg_requester').replace('{name}', this.user?.name || this.t('balance-not_informed'));
            const details = this.t('balance-whatsapp_msg_details_extra').replace('{credits}', this.amount || 0);
            
            const msg = `${this.t('balance-whatsapp_msg_intro')}\n\n${establishment}\n${requester}\n\n${details}.`;
            
            window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
            this.close();
        }
    },
};
</script>

<style scoped lang="scss">
.form-group {
    margin-bottom: 20px;

    label {
        display: block;
        font-size: 14px;
        font-weight: 500;
        color: #111827;
        margin-bottom: 8px;
    }

    input[type="text"], 
    input[type="number"], 
    input[type="tel"] {
        border: 1px solid #E5E7EB;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 14px;
        width: 100%;
    }
}

.gap-md { gap: 16px; }
</style>
